/**
 * Rax Skill Capability Schema
 *
 * Every skill must provide this metadata for transparency.
 * This powers the Capability Card UI.
 */

/**
 * Access level for a skill permission
 */
export type SkillAccessLevel = "read" | "write" | "admin";

/**
 * A single permission/scope the skill requires
 */
export interface SkillAccess {
  /** The scope identifier (e.g., "inbox.read", "calendar.write") */
  scope: string;

  /** Human-readable description of what this access allows */
  description: string;

  /** Level of access: read (view only), write (modify), admin (full control) */
  level: SkillAccessLevel;
}

/**
 * Data retention/handling rule
 */
export interface SkillDataRule {
  /** Type of rule */
  type: "retention" | "exclusion" | "encryption" | "pii";

  /** Emoji icon for display */
  icon: string;

  /** Human-readable rule text */
  text: string;
}

/**
 * Full capability metadata for a skill
 */
export interface SkillCapability {
  /** Unique skill identifier */
  id: string;

  /** Display name */
  name: string;

  /** Short description (one line) */
  description: string;

  /** Emoji or icon identifier */
  icon: string;

  /** Full description for detail view */
  longDescription?: string;

  /** Category for grouping */
  category: "communication" | "productivity" | "data" | "automation" | "integration" | "other";

  /**
   * What the skill accesses
   * Be explicit and comprehensive - this builds trust
   */
  accesses: SkillAccess[];

  /**
   * Value chain: how data flows through the skill
   * e.g., ["Gmail API", "Agent reads", "Summarizes", "Notifies you"]
   */
  valueChain: string[];

  /**
   * Example prompts/scenarios showing what the skill enables
   * 3-5 real examples users might say
   */
  scenarios: string[];

  /**
   * Data handling rules
   * Be transparent about retention, PII handling, encryption
   */
  dataRules: SkillDataRule[];

  /** Does this skill require OAuth or external auth? */
  requiresAuth: boolean;

  /** OAuth provider if applicable */
  authProvider?: "google" | "microsoft" | "slack" | "notion" | "github" | "custom";

  /** OAuth scopes required */
  authScopes?: string[];

  /** Documentation URL */
  docsUrl?: string;

  /** Version of the skill */
  version: string;
}

/**
 * Example skill capabilities for reference
 */
export const EXAMPLE_CAPABILITIES: Record<string, SkillCapability> = {
  gmail: {
    id: "gmail",
    name: "Gmail",
    description: "Read and send emails from your Gmail account",
    icon: "📧",
    longDescription:
      "Connect your Gmail to let your agent read your inbox, search emails, draft replies, and send messages on your behalf.",
    category: "communication",
    accesses: [
      {
        scope: "inbox.read",
        description: "Read your inbox (unread count, subjects, message bodies)",
        level: "read",
      },
      {
        scope: "inbox.search",
        description: "Search through your email history",
        level: "read",
      },
      {
        scope: "mail.send",
        description: "Send emails on your behalf",
        level: "write",
      },
      {
        scope: "labels.manage",
        description: "Create and apply labels to organize emails",
        level: "write",
      },
    ],
    valueChain: ["Gmail API", "Agent reads", "Summarizes", "Notifies you"],
    scenarios: [
      "Hey, any urgent emails?",
      "Draft a reply to Sarah's last message",
      "Find all invoices from Q4",
      "What did the client say about the proposal?",
      "Send a meeting reminder to the team",
    ],
    dataRules: [
      {
        type: "retention",
        icon: "⚠️",
        text: "Email content cached for 24h, then purged",
      },
      {
        type: "exclusion",
        icon: "🔒",
        text: "Never stored: passwords, SSNs, payment info",
      },
      {
        type: "encryption",
        icon: "🔐",
        text: "All data encrypted in transit and at rest",
      },
    ],
    requiresAuth: true,
    authProvider: "google",
    authScopes: [
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/gmail.send",
      "https://www.googleapis.com/auth/gmail.labels",
    ],
    version: "1.0.0",
  },

  calendar: {
    id: "calendar",
    name: "Google Calendar",
    description: "View and manage your calendar events",
    icon: "📅",
    category: "productivity",
    accesses: [
      {
        scope: "events.read",
        description: "View your calendar events",
        level: "read",
      },
      {
        scope: "events.write",
        description: "Create and modify events",
        level: "write",
      },
      {
        scope: "freebusy.read",
        description: "Check your availability",
        level: "read",
      },
    ],
    valueChain: ["Calendar API", "Agent reads", "Analyzes schedule", "Responds to you"],
    scenarios: [
      "What's on my calendar today?",
      "When am I free this week?",
      "Schedule a meeting with John for 30 minutes",
      "Move my 3pm to 4pm",
      "Cancel all meetings tomorrow",
    ],
    dataRules: [
      {
        type: "retention",
        icon: "⚠️",
        text: "Event data cached for current day only",
      },
      {
        type: "pii",
        icon: "👤",
        text: "Attendee emails visible but not stored long-term",
      },
    ],
    requiresAuth: true,
    authProvider: "google",
    authScopes: [
      "https://www.googleapis.com/auth/calendar.readonly",
      "https://www.googleapis.com/auth/calendar.events",
    ],
    version: "1.0.0",
  },

  slack: {
    id: "slack",
    name: "Slack",
    description: "Read channels, summarize updates, and draft/send messages",
    icon: "💬",
    category: "communication",
    accesses: [
      {
        scope: "channels.read",
        description: "Read messages in connected channels",
        level: "read",
      },
      {
        scope: "channels.search",
        description: "Search messages across connected workspaces",
        level: "read",
      },
      {
        scope: "chat.write",
        description: "Post and reply to messages on your behalf",
        level: "write",
      },
    ],
    valueChain: ["Slack API", "Agent reads", "Summarizes context", "Replies or alerts"],
    scenarios: [
      "Summarize what changed in #eng today",
      "Draft a response to the launch thread",
      "Alert me if leadership mentions roadmap risk",
      "Find the last decision about pricing",
    ],
    dataRules: [
      {
        type: "retention",
        icon: "⚠️",
        text: "Message snippets cached briefly for active conversations",
      },
      {
        type: "exclusion",
        icon: "🔒",
        text: "Credentials and raw tokens are never shown in chat",
      },
    ],
    requiresAuth: true,
    authProvider: "slack",
    authScopes: ["channels:history", "channels:read", "chat:write"],
    version: "1.0.0",
  },

  notion: {
    id: "notion",
    name: "Notion",
    description: "Search, summarize, and update connected Notion pages",
    icon: "📝",
    category: "productivity",
    accesses: [
      {
        scope: "pages.read",
        description: "Read page and database content from connected workspaces",
        level: "read",
      },
      {
        scope: "pages.search",
        description: "Search pages and docs by title/content",
        level: "read",
      },
      {
        scope: "pages.write",
        description: "Create or edit pages where access is granted",
        level: "write",
      },
    ],
    valueChain: ["Notion API", "Agent retrieves context", "Synthesizes", "Updates docs/tasks"],
    scenarios: [
      "Find the latest product requirements doc",
      "Summarize my project status pages",
      "Create a meeting notes page for today",
      "Update the launch checklist with completed items",
    ],
    dataRules: [
      {
        type: "retention",
        icon: "⚠️",
        text: "Fetched page content cached only for active task context",
      },
      {
        type: "encryption",
        icon: "🔐",
        text: "Workspace data is encrypted in transit and at rest",
      },
    ],
    requiresAuth: true,
    authProvider: "notion",
    authScopes: ["read_content", "update_content"],
    version: "1.0.0",
  },

  memory: {
    id: "memory",
    name: "Memory",
    description: "Remember things across conversations",
    icon: "🧠",
    category: "productivity",
    accesses: [
      {
        scope: "memory.read",
        description: "Read your saved notes and memories",
        level: "read",
      },
      {
        scope: "memory.write",
        description: "Save new information to memory",
        level: "write",
      },
    ],
    valueChain: ["You tell agent", "Agent stores", "Agent recalls later"],
    scenarios: [
      "Remember that I prefer morning meetings",
      "What did I tell you about the project?",
      "My wife's birthday is March 15th",
      "Forget what I told you about preferences",
    ],
    dataRules: [
      {
        type: "retention",
        icon: "♾️",
        text: "Memories persist until you delete them",
      },
      {
        type: "encryption",
        icon: "🔐",
        text: "All memories encrypted at rest",
      },
      {
        type: "exclusion",
        icon: "🚫",
        text: "You can delete any memory anytime",
      },
    ],
    requiresAuth: false,
    version: "1.0.0",
  },

  web_search: {
    id: "web_search",
    name: "Web Search",
    description: "Search the web for information",
    icon: "🔍",
    category: "data",
    accesses: [
      {
        scope: "search.execute",
        description: "Perform web searches",
        level: "read",
      },
    ],
    valueChain: ["Your question", "Search API", "Results filtered", "Answer synthesized"],
    scenarios: [
      "What's the weather in Tokyo?",
      "Latest news about AI",
      "How do I cook risotto?",
      "What time is it in London?",
    ],
    dataRules: [
      {
        type: "retention",
        icon: "🚫",
        text: "Search queries not stored after response",
      },
      {
        type: "pii",
        icon: "👤",
        text: "No personal data sent to search provider",
      },
    ],
    requiresAuth: false,
    version: "1.0.0",
  },
};

/**
 * Validate a skill capability object
 */
export function validateCapability(cap: Partial<SkillCapability>): string[] {
  const errors: string[] = [];

  if (!cap.id) errors.push("Missing id");
  if (!cap.name) errors.push("Missing name");
  if (!cap.description) errors.push("Missing description");
  if (!cap.icon) errors.push("Missing icon");
  if (!cap.accesses || cap.accesses.length === 0) errors.push("Missing accesses");
  if (!cap.valueChain || cap.valueChain.length === 0) errors.push("Missing valueChain");
  if (!cap.scenarios || cap.scenarios.length === 0) errors.push("Missing scenarios");
  if (!cap.dataRules || cap.dataRules.length === 0) errors.push("Missing dataRules");
  if (cap.requiresAuth === undefined) errors.push("Missing requiresAuth");
  if (!cap.version) errors.push("Missing version");

  // Validate scenarios have enough examples
  if (cap.scenarios && cap.scenarios.length < 3) {
    errors.push("Need at least 3 scenarios");
  }

  // Validate accesses have proper structure
  if (cap.accesses) {
    for (const access of cap.accesses) {
      if (!access.scope || !access.description || !access.level) {
        errors.push(`Invalid access: ${JSON.stringify(access)}`);
      }
    }
  }

  return errors;
}
